import { lazy, Suspense, useEffect, useRef, type ReactNode } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
// Public surfaces ship in the main bundle: they are what an anonymous
// visitor (often on a phone) hits first, so they must not wait on a
// second request.
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DebtorLoginPage from './pages/DebtorLoginPage';
import RequestPasswordResetPage from './pages/RequestPasswordResetPage';
import CompletePasswordResetPage from './pages/CompletePasswordResetPage';
import SignupPage from './pages/SignupPage';
import CreditorSignupPage from './pages/CreditorSignupPage';
import AboutLenderlyPage from './pages/AboutLenderlyPage';
import ProtectedRoute from './components/ProtectedRoute';
import { LanguageToggle } from './components/LanguageToggle';
import { NavOverflow } from './components/NavOverflow';
import { MobileNav } from './components/MobileNav';
import { useMediaQuery } from './hooks/useMediaQuery';
import { useSignOut } from './hooks/useSignOut';
import { HatToggle } from './components/HatToggle';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { useAuthStore, useImpersonationTransition } from './store/authStore';
import { getAwaitingOffersCount, getMe, getPendingCreditorLinkCount } from './services/api';

// Signed-in surfaces are code-split per route. The operator console
// alone (entry detail + purchase-offer panel + open-entry form) is
// several hundred KB of JS that a debtor checking their case on a
// phone, or a visitor on the landing page, never needs to download or
// parse. Each page becomes its own chunk that loads on first visit.
const LookupPage = lazy(() => import('./pages/LookupPage'));
const OpenEntryPage = lazy(() => import('./pages/OpenEntryPage'));
const EntriesPage = lazy(() => import('./pages/EntriesPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PaymentMethodsPage = lazy(() => import('./pages/PaymentMethodsPage'));
const EntryDetailPage = lazy(() => import('./pages/EntryDetailPage'));
const PendingApprovalPage = lazy(() => import('./pages/PendingApprovalPage'));
const PendingLendersPage = lazy(() => import('./pages/PendingLendersPage'));
const LendersPage = lazy(() => import('./pages/LendersPage'));
const OperatorsPage = lazy(() => import('./pages/OperatorsPage'));
const InviteLenderPage = lazy(() => import('./pages/InviteLenderPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const DebtorCasesPage = lazy(() => import('./pages/DebtorCasesPage'));
const PurchaseOffersQueuePage = lazy(
  () => import('./pages/PurchaseOffersQueuePage'),
);
const PlatformFeeLedgerPage = lazy(
  () => import('./pages/PlatformFeeLedgerPage'),
);
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const LenderAccountPage = lazy(() => import('./pages/LenderAccountPage'));
const AccountsRollupPage = lazy(() => import('./pages/AccountsRollupPage'));
const ConnectPlatformPage = lazy(() => import('./pages/ConnectPlatformPage'));

export default function App() {
  const location = useLocation();
  // Changes exactly when a login-as session starts or ends (and never
  // goes back to an earlier value). Part of the page wrapper's key below,
  // so that every page — and every bit of state it holds — is thrown away
  // when the identity behind it changes, not only the query cache (see
  // IdentityGate for why the old page must not simply be left to render
  // on).
  const identityEpoch = useAuthStore((s) => s.identityEpoch);

  return (
    // `overflow-x-clip`, not `-hidden`: hidden turns this box into a
    // scroll container that never scrolls, and a sticky child (the
    // login-as banner) then sticks to that box instead of the viewport.
    // Clip cuts the same overflow without becoming a scroll container.
    <div className="min-h-screen flex flex-col overflow-x-clip">
      <ImpersonationBanner />
      <RegistryHeader />
      <main className="flex-1 mx-auto w-full max-w-[1400px] px-[var(--page-gutter)] py-8 sm:py-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${location.pathname}|${identityEpoch}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.24, ease: [0.2, 0, 0, 1] }}
          >
            {/* The Suspense boundary sits INSIDE the route transition
                wrapper so the exit animation of the previous page still
                plays while a not-yet-loaded chunk is fetched. */}
            <Suspense fallback={<RouteFallback />}>
            <IdentityGate>
            <Routes location={location}>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              {/*
                Public debtor login. Lexta hands the debtor a case
                number out-of-band (operator handover or email later);
                the debtor types it together with their CPR. Backend
                hashes the CPR, looks up the matching entry, and
                issues a debtor JWT scoped to that subject_key. After
                success the page navigates to /min-sag — the existing
                debtor-cases route.
              */}
              <Route path="/debtor-login" element={<DebtorLoginPage />} />
              {/*
                Public password-reset entry. Linked from
                /login (the "Forgot password?" affordance). The
                page hits POST /auth/password-reset/request and
                always shows the same generic "if an account
                exists, a link is on its way" confirmation —
                same enumeration-safe rule the login endpoint
                uses.
              */}
              <Route
                path="/forgot-password"
                element={<RequestPasswordResetPage />}
              />
              {/*
                Public reset-completion. Reached via the email
                link generated by the reset-request endpoint:
                /reset-password?token=<plaintext>. On mount the
                page validates the token; on submit it completes
                the reset and drops the auto-issued JWT into the
                auth store.
              */}
              <Route
                path="/reset-password"
                element={<CompletePasswordResetPage />}
              />
              {/*
                Public invite-acceptance page. Reached via the
                `/signup?token=...` URL the operator emails to a
                new lender_human. The page itself reads the token
                from the query string, fetches a preview, and on
                successful accept drops the auto-issued JWT into the
                auth store. ProtectedRoute is intentionally absent —
                anonymous visitors must reach it.
              */}
              <Route path="/signup" element={<SignupPage />} />
              {/*
                Public creditor self-signup. Anonymous visitors land
                here from a small CTA on the login page when they
                don't have an invite. On submit the backend creates
                a lender row in `review_status: "pending_review"` +
                an admin lender_human user and signs them in. They
                bounce to /pending-approval until an operator
                approves the lender.
              */}
              <Route path="/signup-creditor" element={<CreditorSignupPage />} />
              {/*
                Public colophon for Lexta's sponsor (Lenderly).
                Reached from the footer's sponsor stripe and (later)
                from the filing-receipt curiosity hook. No
                ProtectedRoute — anonymous visitors must reach it.
              */}
              <Route path="/om-lenderly" element={<AboutLenderlyPage />} />
              {/*
                Pending-approval landing for a self-signed creditor's
                admin user. lender_human only — operators / debtors
                hitting the URL get bounced to their natural home.
              */}
              <Route
                path="/pending-approval"
                element={
                  <ProtectedRoute allowedRoles={['lender_human']}>
                    <PendingApprovalPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Creditor-link connect page. Reached via a one-time
                `/connect/<code>` link a filing platform hands the real
                creditor when Lexta couldn't match her by e-mail alone
                (see the creditor-attribution design doc). lender_human
                only — an anonymous visitor is bounced through /login
                first (state.from preserved) and lands back here.
              */}
              <Route
                path="/connect/:code"
                element={
                  <ProtectedRoute allowedRoles={['lender_human', 'operator']}>
                    <ConnectPlatformPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Operator-only review console. Backend mirrors this
                gate via `require_operator`; lender_humans / debtors
                get bounced back to their natural home by
                ProtectedRoute.
              */}
              <Route
                path="/team/pending-lenders"
                element={
                  <ProtectedRoute allowedRoles={['operator']}>
                    <PendingLendersPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Invite-issuance console — operator + lender_human.
                Operators issue cross-tenant invites; approved
                lender_human admins issue colleague invites for their
                OWN lender (the page itself toggles the picker on
                role). Backend gates via inline operator-or-own-lender
                rule; pending / rejected lender_humans are refused
                server-side, debtors are bounced by ProtectedRoute.
              */}
              <Route
                path="/team/invite"
                element={
                  <ProtectedRoute
                    allowedRoles={['operator', 'lender_human']}
                  >
                    <InviteLenderPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Operator-only lenders directory + per-lender policy
                override console. Lists every registered lender with
                a status badge and a `policy_override` pill; the
                operator flips an individual lender between the
                platform default, hard-block, and soft-warn from here.
                Backend mirrors the gate via `require_operator`.
              */}
              <Route
                path="/team/lenders"
                element={
                  <ProtectedRoute allowedRoles={['operator']}>
                    <LendersPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Operator-only operators directory. Lists every Lexta
                operator account (active + deactivated) and every
                pending operator invite, with deactivate / reactivate
                and revoke-invite actions. Backend mirrors the gate via
                `require_operator`.
              */}
              <Route
                path="/team/operators"
                element={
                  <ProtectedRoute allowedRoles={['operator']}>
                    <OperatorsPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Operator-only audit log viewer. Reads every audit row
                across all flows (register, dispute, invite, api_key,
                creditor signup) for compliance / "who did what to
                which lender, when". Backend mirrors the gate via
                `require_operator`; lender_humans / debtors hit
                ProtectedRoute's wrong-role redirect.
              */}
              <Route
                path="/team/audit"
                element={
                  <ProtectedRoute allowedRoles={['operator']}>
                    <AuditLogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lookup"
                element={
                  <ProtectedRoute allowedRoles={['operator', 'lender_human']}>
                    <LookupPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/entries"
                element={
                  <ProtectedRoute allowedRoles={['operator', 'lender_human']}>
                    <EntriesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/entries/open"
                element={
                  <ProtectedRoute allowedRoles={['operator', 'lender_human']}>
                    <OpenEntryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/entries/:id"
                element={
                  <ProtectedRoute allowedRoles={['operator', 'lender_human']}>
                    <EntryDetailPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Debt-purchase negotiation queue. Operator + lender_human
                only — debtors hit ProtectedRoute's wrong-role redirect
                back to /min-sag. The page itself does role-scoped
                client-side narrowing on top of the backend RBAC, so
                lender_humans see a "your turn" tab that filters down to
                offers awaiting a lender response.
              */}
              <Route
                path="/offers"
                element={
                  <ProtectedRoute allowedRoles={['operator', 'lender_human']}>
                    <PurchaseOffersQueuePage />
                  </ProtectedRoute>
                }
              />
              {/*
                Platform settings — the auto-sale price, the platform
                fee, the default frist and settlement mode. Every
                operator may read them; the page itself and the backend
                restrict writing to administrators.
              */}
              <Route
                path="/team/settings"
                element={
                  <ProtectedRoute allowedRoles={['operator']}>
                    <AdminSettingsPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Per-lender account (auto-sale design): what a lender
                filed, sold to Lexta, collected on Lexta's behalf and
                holds for Lexta, month by month. Operators open any
                lender's; a lender opens its own at /account.
              */}
              <Route
                path="/lenders/:id/account"
                element={
                  <ProtectedRoute allowedRoles={['operator']}>
                    <LenderAccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute allowedRoles={['lender_human']}>
                    <LenderAccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team/accounts"
                element={
                  <ProtectedRoute allowedRoles={['operator']}>
                    <AccountsRollupPage />
                  </ProtectedRoute>
                }
              />
              {/* Platform fee ledger — operator only (Lexta-internal
                  bookkeeping of the fee booked on each purchase). */}
              <Route
                path="/offers/fees"
                element={
                  <ProtectedRoute allowedRoles={['operator']}>
                    <PlatformFeeLedgerPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Self-service API-keys management. Operator + lender_human
                only — debtors hit ProtectedRoute's wrong-role redirect
                back to /min-sag. Lender_humans auto-scope to their own
                lender_id (read from the JWT); operators get a lender
                picker so they can manage any lender's keys.
              */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute allowedRoles={['operator', 'lender_human']}>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              {/* The lender's own payment methods ("Betalingsmuligheder").
                  Operators keep any lender's from /team/lenders. */}
              <Route
                path="/payment-methods"
                element={
                  <ProtectedRoute allowedRoles={['lender_human']}>
                    <PaymentMethodsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api-keys"
                element={
                  <ProtectedRoute allowedRoles={['operator', 'lender_human']}>
                    <ApiKeysPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Debtor self-service. URL is intentionally Danish ("min sag"
                = "my case") and not internationalised — the route is
                stable identity, the page contents are i18n'd. Debtors
                cannot reach any of the lender / operator routes; the
                guard above bounces them back here.
              */}
              <Route
                path="/min-sag"
                element={
                  <ProtectedRoute allowedRoles={['debtor']}>
                    <DebtorCasesPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
            </IdentityGate>
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>
      <RegistryFooter />
    </div>
  );
}

/**
 * Keeps pages from ever being rendered as the wrong identity when a
 * login-as session starts or ends.
 *
 *  * From the moment the identity flips until the SPA has emptied its
 *    query cache and moved to the page it is going to (ImpersonationBanner
 *    acknowledges the transition then), nothing is rendered.
 *  * A page that is animating out when the flip happens is still mounted
 *    and still subscribed to the store (AnimatePresence keeps the last
 *    element it rendered): left alone it would render again as the NEW
 *    identity at the OLD address, ask the API for things and have
 *    ProtectedRoute bounce it through /lookup. The gate remembers the
 *    identity epoch it was mounted under (the wrapper above it is keyed
 *    on the epoch, so the new page gets a new gate) and shows the
 *    fallback from then on.
 *
 * It compares EPOCHS, not session ids: the id is null before a session
 * and null again after it, so a page from before the session that is
 * still animating out when a quick session ends would match the present
 * again, come back to life as the signed-out identity and, through its
 * ProtectedRoute, redirect-loop. An epoch is never seen twice.
 */
function IdentityGate({ children }: { children: ReactNode }) {
  const identity = useAuthStore((s) => s.identityEpoch);
  const switching = useImpersonationTransition((s) => s.transition !== null);
  const mountedUnder = useRef(identity);
  return switching || mountedUnder.current !== identity ? (
    <RouteFallback />
  ) : (
    <>{children}</>
  );
}

/**
 * Shown while a code-split page chunk is being fetched. Same quiet mono
 * "working" line the pages themselves use while their data loads, so a
 * first visit to a route reads as one continuous loading state.
 */
function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="font-mono text-xs uppercase tracking-[0.14em] text-[var(--color-ink-muted)]"
    >
      {t('common.loading')}
    </div>
  );
}

/**
 * The links that stay inline at every desktop width: the daily-use
 * four (lookup, open entry, entries, negotiations).
 */
const DAILY_NAV_COUNT = 4;

/**
 * Links lifted out of "Mere ▾" onto the inline row once there is width
 * for them (``xl`` and up — see `primaryCount`). Listed by route, so
 * the promotion survives a reshuffle of `navItems` and so a role that
 * has no such route simply promotes nothing: an operator promotes the
 * lenders directory + platform settings, a lender_human its own
 * account, a debtor neither.
 */
const PROMOTED_ROUTES = ['/account', '/team/lenders', '/team/settings'];

function RegistryHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  // One sign-out for the desktop button and the phone menu: inside a
  // login-as session it ends the session (label "Afslut") instead of
  // signing the admin out. See useSignOut.
  const { signOut, label: signOutLabel } = useSignOut();
  const setUser = useAuthStore((s) => s.setUser);
  // Tailwind's ``xl``. Read in JS because it decides how `navItems` is
  // split, not how a rendered element looks — see `primaryCount`.
  const wideDesktop = useMediaQuery('(min-width: 1280px)');
  const canSwitchHat = useAuthStore((s) => s.canSwitchHat);

  const isAuthenticated = Boolean(token);

  // Refresh the cached user record once per session so a promotion or
  // demotion made elsewhere shows up without a re-login. Display only:
  // every admin action is re-checked server-side against the database.
  const meQuery = useQuery({
    queryKey: ['me', user?.id ?? null],
    queryFn: getMe,
    enabled: isAuthenticated && Boolean(user?.id),
    staleTime: 60_000,
    retry: false,
  });
  useEffect(() => {
    const fresh = meQuery.data;
    if (!fresh || !user || fresh.id !== user.id) return;
    // Effective admin flag plus the account-level hat fields: a home
    // lender assigned (or removed) elsewhere shows the switch without a
    // re-login, and a store hydrated from an older response gets the
    // fields at all. `role` itself follows the token and is left alone.
    const nextAdmin = fresh.role === 'operator' && fresh.is_admin === true;
    const nextAccountRole = fresh.account_role ?? user.account_role;
    const nextAccountAdmin = fresh.account_is_admin ?? user.account_is_admin;
    const nextHat = fresh.hat ?? user.hat ?? null;
    const nextHome = fresh.home_lender_id ?? null;
    if (
      (user.is_admin === true) !== nextAdmin ||
      user.account_role !== nextAccountRole ||
      (user.account_is_admin ?? false) !== (nextAccountAdmin ?? false) ||
      (user.hat ?? null) !== nextHat ||
      (user.home_lender_id ?? null) !== nextHome
    ) {
      setUser({
        ...user,
        is_admin: nextAdmin,
        account_role: nextAccountRole,
        account_is_admin: nextAccountAdmin,
        hat: nextHat,
        home_lender_id: nextHome,
      });
    }
  }, [meQuery.data, user, setUser]);

  // Nav items are role-scoped:
  // - Anonymous: nothing — sign-in CTA only.
  // - Debtor: a single "Mine sager" link to /min-sag. The lookup /
  //   entries pages are server-side denied for debtor JWTs anyway, so
  //   we don't dangle a link they can't follow.
  // - Operator / lender_human: the existing lookup / open-entry /
  //   entries triplet.
  // Live count of purchase offers that need this user's next move —
  // rendered as an amber badge on "Forhandlinger". Polled every minute;
  // offer mutations invalidate the key so the badge updates at once.
  const canNegotiate =
    isAuthenticated &&
    (user?.role === 'operator' || user?.role === 'lender_human');
  const awaitingQuery = useQuery({
    // Scoped to the user so a still-fresh count from a previous
    // session on the same browser is never shown to the next login.
    queryKey: ['offers-awaiting-count', user?.id ?? null],
    queryFn: getAwaitingOffersCount,
    enabled: canNegotiate,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });
  const awaitingCount = canNegotiate ? (awaitingQuery.data ?? 0) : 0;

  // Pending creditor-link claims — platform filings a lender_human can
  // accept/decline (see PendingClaimsBanner on /entries). Surfaced as
  // the same amber badge, on "Poster" this time, so the count is
  // visible without opening the page. Operator-only and debtor
  // sessions never fetch this (they have no candidate links).
  // Operators see every pending link (they attribute for lenders who
  // have no login of their own), so they get the badge too.
  const seesPendingLinks =
    isAuthenticated && (user?.role === 'lender_human' || user?.role === 'operator');
  const pendingLinksQuery = useQuery({
    queryKey: ['creditor-links-pending-count', user?.id ?? null],
    queryFn: getPendingCreditorLinkCount,
    enabled: seesPendingLinks,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });
  const pendingLinksCount = seesPendingLinks ? (pendingLinksQuery.data ?? 0) : 0;

  const navItems: { to: string; label: string; badge?: number }[] = !isAuthenticated
    ? []
    : user?.role === 'debtor'
      ? [{ to: '/min-sag', label: t('debtor.label') }]
      : [
          { to: '/lookup', label: t('header.lookup') },
          { to: '/entries/open', label: t('header.openEntry') },
          {
            to: '/entries',
            label: t('header.entries'),
            badge: pendingLinksCount,
          },
          // Debt-purchase negotiations — visible to operator +
          // lender_human; backend RBAC gates the queue listing
          // server-side. Hidden from debtors (they don't see this
          // branch of `navItems` at all). Carries the awaiting-you
          // badge.
          { to: '/offers', label: t('header.offers'), badge: awaitingCount },
          // ---- Promoted (see PROMOTED_ROUTES / primaryCount) --------
          // These sit directly after the daily-use four so one slice
          // covers "daily four + promoted", and so the phone menu lists
          // them in the same place the wide header shows them.
          //
          // The lender's own account — what it holds for Lexta and
          // what it is owed. Operators have the rollup under "Mere".
          ...(user?.role === 'lender_human'
            ? [{ to: '/account', label: t('header.account') }]
            : []),
          ...(user?.role === 'operator'
            ? [
                // The lenders directory — the operator's most-opened
                // register page after the daily four.
                { to: '/team/lenders', label: t('header.lenders') },
                // Platform settings — read by every operator, changed
                // by administrators only.
                { to: '/team/settings', label: t('header.settings') },
              ]
            : []),
          // ---- End promoted -----------------------------------------
          // Where the lender's debtors pay — stated once by the lender,
          // picked per entry. First in the lender's "Mere" menu.
          ...(user?.role === 'lender_human'
            ? [{ to: '/payment-methods', label: t('header.paymentMethods') }]
            : []),
          // Self-service API-keys management — visible to operator +
          // lender_human. Lender_humans auto-scope to their own lender;
          // operators get a lender picker on the page itself. Hidden
          // from debtors.
          { to: '/api-keys', label: t('header.apiKeys') },
          // Invite-issuance — visible to operator AND lender_human.
          // Operator label reads "Invitér långiver" (cross-tenant
          // operator action); lender_human label reads "Invitér
          // kollega" (own-lender colleague onboarding). Backend
          // enforces the ownership rule; pending / rejected lender
          // admins are refused server-side. Hidden from debtors.
          {
            to: '/team/invite',
            label:
              user?.role === 'operator'
                ? t('header.inviteLender')
                : t('header.inviteColleague'),
          },
          // Operator-only — the pending-approval review console and the
          // rest of the operator chrome. Sits to the right of the
          // shared invite link so the operator-only links read as a
          // contiguous block (the lenders directory and the settings
          // page are the exception: both are promoted above). Backend
          // gates each via `require_operator`; the link is hidden from
          // non-operators here purely to keep the chrome tidy.
          ...(user?.role === 'operator'
            ? [
                {
                  to: '/team/pending-lenders',
                  label: t('header.pendingLenders'),
                },
                // Every lender's account, summed — what the lenders
                // hold for Lexta right now.
                { to: '/team/accounts', label: t('header.accounts') },
                // Operators directory — who runs the register, the
                // counterpart to the promoted lenders directory.
                { to: '/team/operators', label: t('header.operators') },
                // Platform fee ledger — Lexta-internal bookkeeping of
                // the fee booked on each purchase. Also linked from
                // the fee card on the negotiations page.
                { to: '/offers/fees', label: t('header.feeLedger') },
                // Audit-log viewer — placed AFTER pending-lenders so
                // operator-only links stay grouped. Lower urgency than
                // the review queue, so it sits rightmost in the operator
                // chrome.
                { to: '/team/audit', label: t('header.audit') },
              ]
            : []),
        ];

  // Two tiers, because the inline row competes with the account
  // cluster for the same line:
  //
  //  * ``lg`` (1024–1279px) keeps the four daily-use links inline —
  //    the row already carries "Mere ▾", the role pill, the language
  //    toggle and sign-out, and anything more wraps.
  //  * ``xl`` (≥1280px) also lifts the promoted links out of the menu:
  //    Långivere + Indstillinger for an operator, Konto for a lender.
  //    Those are the standing reference pages — who is on the register,
  //    and the rules it runs on — and they were worth a click less than
  //    the rest of the admin chrome. The row pays for them by holding
  //    the e-mail back to ``2xl`` (see the account cluster below).
  //
  // Counted by route, never by index, and the promoted entries are
  // built directly after the daily four, so ``slice(0, primaryCount)``
  // is exactly "daily four + promoted" and the remainder is the menu.
  const primaryCount =
    DAILY_NAV_COUNT +
    (wideDesktop
      ? navItems.filter((i) => PROMOTED_ROUTES.includes(i.to)).length
      : 0);

  // Every signed-in human gets their profile in the menu; debtors have
  // /min-sag instead. Pushed after the role lists so it lands last (in
  // the overflow on narrow desktops, in the phone menu on mobile).
  if (isAuthenticated && user && user.role !== 'debtor') {
    navItems.push({ to: '/profile', label: t('header.profile') });
  }

  // Exactly ONE nav item is active: the longest `to` that is the current
  // path or a parent of it. Plain prefix matching lit up both "Poster"
  // (/entries) and "Opret post" (/entries/open) on the open-entry page,
  // and because the underline is a shared layout element it then slid
  // between the two — the "jumps to Poster" effect.
  const activeTo =
    navItems
      .map((i) => i.to)
      .filter(
        (to) =>
          location.pathname === to ||
          (to !== '/' && location.pathname.startsWith(`${to}/`)),
      )
      .sort((a, b) => b.length - a.length)[0] ?? null;

  const roleLabel = user
    ? user.role === 'operator'
      ? user.is_admin
        ? t('auth.roleAdmin')
        : t('auth.roleOperator')
      : user.role === 'debtor'
        ? t('auth.roleDebtor')
        : t('auth.roleLenderHuman')
    : null;

  return (
    <motion.header
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.22, ease: [0.2, 0, 0, 1] }}
      // Clean white header so the slate-and-blue logo reads naturally.
      // A 3px brand-blue accent stripe runs along the bottom of the
      // header to anchor the brand presence without the heavy navy
      // bar that competed with the rest of the page.
      //
      // ``relative z-40`` gives the phone menu (MobileNav) an anchor for
      // its drop-down panel and a stacking context above page content.
      className="relative z-40 bg-white border-b-[3px] border-[var(--color-primary)]"
    >
      <div className="mx-auto max-w-[1400px] px-[var(--page-gutter)]">
        <div className="relative z-20 flex items-center justify-between gap-3 py-3.5 lg:py-5">
          <Link to="/" className="flex items-center gap-3 group shrink-0" aria-label="Lexta">
            <img
              src="/lexta-logo.png"
              alt=""
              aria-hidden="true"
              draggable={false}
              className="h-9 lg:h-10 w-auto select-none"
            />
          </Link>

          {/*
            Split navItems into "primary" (kept inline — daily-use
            actions, plus the promoted ones once the window is ``xl``)
            and "overflow" (collapsed under a "Mere ▾" dropdown —
            admin/secondary actions). The cutoff is `primaryCount`,
            chosen so the operator chrome (a dozen entries when fully
            expanded) stays on a single line at common laptop widths
            instead of wrapping. Roles with shorter navItems (debtor:
            1 entry) naturally land everything in primary.

            The whole inline row is desktop-only (``hidden lg:flex``):
            below ``lg`` it is replaced by the compact controls further
            down — language toggle + MobileNav menu button (signed in)
            or language toggle + sign-in CTA (anonymous).
          */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.slice(0, primaryCount).map((item) => {
              const active = item.to === activeTo;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`relative px-3 py-1.5 text-sm transition-colors ${
                    active
                      ? 'text-[var(--color-ink)] font-medium'
                      : 'text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]'
                  }`}
                >
                  {item.label}
                  {item.badge ? (
                    <span
                      aria-label={t('header.offersBadge', { count: item.badge })}
                      className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--color-alert)] text-white text-[10px] font-mono tabular align-middle"
                    >
                      {item.badge}
                    </span>
                  ) : null}
                  {active && (
                    <motion.span
                      layoutId="nav-underline"
                      // Brighter blue underline (logo-blue tone) so the
                      // active route still reads against the deep-navy
                      // header surface. Slightly thicker than the prior
                      // 1px hairline to compensate for the dark backdrop.
                      className="absolute left-3 right-3 -bottom-px h-[2px] bg-[var(--color-primary)]"
                      transition={{
                        type: 'tween',
                        duration: 0.22,
                        ease: [0.2, 0, 0, 1],
                      }}
                    />
                  )}
                </Link>
              );
            })}
            {navItems.length > primaryCount && (
              <NavOverflow
                items={navItems.slice(primaryCount)}
                activeTo={activeTo}
              />
            )}

            {isAuthenticated ? (
              <div className="flex items-center gap-3 ml-3 pl-3 border-l border-[var(--color-rule)]">
                {user && (
                  <div className="flex items-center gap-2">
                    {/* An operator with a home lender gets the two-hat
                        switch where everyone else sees their role pill. */}
                    {canSwitchHat() ? (
                      <HatToggle />
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-primary-strong)] px-1.5 py-0.5 bg-[var(--color-primary-soft)]">
                        {roleLabel}
                      </span>
                    )}
                    {/* The e-mail is the first thing to yield when the
                        row gets crowded: from ``xl`` the inline row
                        also carries the promoted links (Långivere +
                        Indstillinger for an operator), and the two
                        together do not fit — an operator's row measures
                        ~1293px against the 1232px a 1280px window
                        leaves. So the e-mail comes back at ``2xl``,
                        where there is room for both. The phone menu
                        shows it in its account block at every width,
                        and the role pill next to it never moves. */}
                    {/* Capped at 12rem and truncated: with six inline links the
                        row has ~40px of slack at 1536px, so an unusually long
                        address must shorten rather than wrap the header. The
                        full address is in the title and on /profile.
                        Hidden altogether for an account that shows the
                        two-hat toggle: the toggle is ~85px wider than the
                        pill and would use up that slack. */}
                    {user.email && !canSwitchHat() && (
                      <Link
                        to="/profile"
                        className="hidden 2xl:inline-block max-w-[12rem] truncate align-bottom font-mono text-xs text-[var(--color-ink-muted)] tabular hover:text-[var(--color-ink)] hover:underline"
                        title={t('header.profile')}
                      >
                        {user.email}
                      </Link>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={signOut}
                  className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-[var(--color-rule)] text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:border-[var(--color-ink)] transition-colors"
                >
                  {signOutLabel}
                </button>
              </div>
            ) : (
              // Anonymous brand entry-point. On the deep-blue header
              // we use a white-on-blue button (inverse of the page-
              // body `btn-primary`) so the CTA still pops against the
              // navy surface without inventing a new component. The
              // arrow-pop on hover is provided by the host page's
              // login link; here we keep the chrome minimal.
              <Link
                to="/login"
                className="btn-primary ml-3 px-4 py-1.5 text-xs font-mono uppercase tracking-wider"
              >
                {t('auth.signIn')}
              </Link>
            )}

            <div className="ml-3 pl-3 border-l border-[var(--color-rule)] flex items-center">
              <LanguageToggle />
            </div>
          </nav>

          {/*
            Phone / tablet controls (``lg:hidden``). Signed-in users get
            the language toggle plus the menu button whose panel lists
            every nav item and the account block; anonymous visitors
            have no nav items, so they keep the sign-in CTA inline.
          */}
          <div className="flex lg:hidden items-center gap-3">
            <LanguageToggle />
            {isAuthenticated ? (
              <MobileNav
                items={navItems}
                activeTo={activeTo}
                roleLabel={roleLabel}
                hatControl={canSwitchHat() ? <HatToggle tall /> : null}
                email={user?.email ?? null}
                onLogout={signOut}
                logoutLabel={signOutLabel}
              />
            ) : (
              <Link
                to="/login"
                className="btn-primary inline-flex items-center min-h-10 px-3.5 py-2 text-xs font-mono uppercase tracking-wider"
              >
                {t('auth.signIn')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.header>
  );
}

function RegistryFooter() {
  const { t } = useTranslation();
  return (
    // Branded surface to bookend the page — soft blue wash so the
    // bottom of the page mirrors the deep-blue header at the top.
    // Hairline border stays slate to keep the rule subtle.
    <footer className="border-t border-[var(--color-rule)] mt-16 sm:mt-24 bg-[var(--color-surface-blue)]">
      <div className="mx-auto max-w-[1400px] px-[var(--page-gutter)] py-8">
        {/* One line of chrome: what this is. No register number and no
            version — the site is the product, not a build artefact. */}
        <div className="section-label">{t('footer.tagline')}</div>
        {/*
          Sponsor stripe — bibliographic colophon line, not a banner.
          Stays in the same hairline mono register as the row above so
          it reads as a record-of-sponsorship rather than promotional
          chrome. The "learn more" half is a plain internal link to
          /om-lenderly (no new tab, no colour shift).
        */}
        <div className="mt-6 pt-4 border-t border-[var(--color-rule)] text-xs font-mono text-[var(--color-ink-soft)] flex flex-wrap items-center gap-y-1">
          <span>{t('footer.sponsorBy')}</span>
          <span className="px-2" aria-hidden="true">·</span>
          <Link to="/om-lenderly" className="hover:underline">
            {t('footer.sponsorLearnMore')}
          </Link>
        </div>
      </div>
    </footer>
  );
}
